import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateNodesFromDOM } from '@lexical/html';
import {
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND
} from 'lexical';

import { convertHtmlTableToGfm } from '../../utils/convertHtmlTableToGfm';

function replaceTablesWithMarkdown(dom: Document): boolean {
  const tables = dom.querySelectorAll('table');
  if (tables.length === 0) return false;

  tables.forEach((tableEl) => {
    const markdown = convertHtmlTableToGfm(tableEl as HTMLTableElement);
    const replacement = dom.createElement('div');

    if (markdown) {
      const lines = markdown.split('\n');
      lines.forEach((line, index) => {
        if (index > 0) replacement.appendChild(dom.createElement('br'));
        replacement.appendChild(dom.createTextNode(line));
      });
    }

    tableEl.replaceWith(replacement);
  });

  return true;
}

function HtmlTablePastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!(event instanceof ClipboardEvent)) return false;

        const html = event.clipboardData?.getData('text/html');
        if (!html || !/<table[\s>]/i.test(html)) return false;

        const dom = new DOMParser().parseFromString(html, 'text/html');
        const replaced = replaceTablesWithMarkdown(dom);
        if (!replaced) return false;

        event.preventDefault();
        event.stopPropagation();

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const nodes = $generateNodesFromDOM(editor, dom);
          $insertNodes(nodes);
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}

export default HtmlTablePastePlugin;
